import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Hourglass } from 'lucide-react';
import { api } from '../../api/client';
import { naviguer } from '../../App';
import type { Demande } from '../../types';
import { formatDateCourt } from './format';
import { TexteSegments } from './BadgeReponse';
import { ligneEcheance } from './confirmation';
import { vueDeLaDemande } from './elementsJour';
import { validationsARefaire } from './parcours';
import { SEUILS_URGENCE_PAR_DEFAUT, type SeuilsUrgence } from './urgence';

import { dePersonne } from '../messages/format';
// « 14:00:00 » → « 14 h » ; « 09:30:00 » → « 9 h 30 »
function heure(t?: string | null): string {
  const m = t ? /^(\d{1,2}):(\d{2})/.exec(t) : null;
  if (!m) return '';
  return m[2] === '00' ? `${Number(m[1])} h` : `${Number(m[1])} h ${m[2]}`;
}

function horaire(d: Demande): string {
  const a = heure(d.atelier_heure_debut);
  const b = heure(d.atelier_heure_fin);
  return a && b ? `${a} – ${b}` : '';
}

/** Où en est la demande, dit à la personne qui remplace — en minuscules pour s'insérer dans une phrase. */
function etapeEnPhrase(d: Demande): string {
  switch (d.statut) {
    case 'validation_responsable': return "validation en cours par l'équipe gestion";
    case 'validation_rh': return 'validation en cours par les RH';
    case 'attente_tipee': return "les RH valident l'absence dans Tipee";
    case 'passation': return `la passation se prépare avec ${d.absent_nom ?? 'la personne absente'}`;
    case 'atelier_donne': return 'la passation est terminée';
    default: return '';
  }
}

/** Où en est un remplacement déjà confirmé. */
function etape(d: Demande): string {
  switch (d.statut) {
    case 'validation_responsable': return "Validation en cours par l'équipe gestion";
    case 'validation_rh': return 'Validation en cours par les RH';
    case 'attente_tipee': return "Les RH valident l'absence dans Tipee";
    case 'passation': return `Passation à préparer avec ${d.absent_nom ?? 'la personne absente'}`;
    case 'atelier_donne': return 'Passation transmise : à lire avant la séance';
    default: return '';
  }
}

type Reponse = 'confirmer_remplacement' | 'refuser_remplacement';
type Choix = { demande: Demande; action: Reponse; desistement: boolean } | null;

/**
 * Bloc « Vos remplacements » du Tableau de bord, au-dessus des onglets : les séances où la personne connectée est désignée remplaçant·e.
 * « À confirmer » demande une réponse (Je confirme / Décliner, sans quitter le tableau de bord) avec la date limite ; « À venir » suit les
 * remplacements confirmés, où l'on peut encore se désister jusqu'à la veille. La demande avance pendant que la réponse est attendue
 * (docs/DECISIONS.md §38). N'affiche rien tant qu'il n'y a rien à montrer.
 */
export function VosRemplacements({ rechargerCle, onChange, seuils = SEUILS_URGENCE_PAR_DEFAUT }: { rechargerCle?: string; onChange?: () => void; seuils?: SeuilsUrgence }) {
  const [demandes, setDemandes] = useState<Demande[]>([]);
  const [choix, setChoix] = useState<Choix>(null);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const charger = useCallback(() => {
    return api.getMesRemplacements().then(setDemandes).catch(() => setDemandes([]));
  }, []);
  useEffect(() => { void charger(); }, [charger, rechargerCle]);

  // Échap ferme la fenêtre (jamais un clic à côté).
  useEffect(() => {
    if (!choix) return;
    const f = (e: KeyboardEvent) => { if (e.key === 'Escape' && !enCours) setChoix(null); };
    document.addEventListener('keydown', f);
    return () => document.removeEventListener('keydown', f);
  }, [choix, enCours]);

  if (demandes.length === 0 && !choix) return null;

  // Le serveur dit où en est la réponse ; l'écran ne la recalcule pas.
  const aConfirmer = demandes.filter((d) => d.confirmation_remplacant?.sous_reserve);
  const aVenir = demandes.filter((d) => !d.confirmation_remplacant?.sous_reserve);

  async function repondre() {
    if (!choix) return;
    setEnCours(true);
    setErreur(null);
    try {
      // La personne que l'écran montrait : si la désignation a changé entre-temps, le serveur refuse au lieu de répondre à la place de la mauvaise.
      await api.appliquerAction(choix.demande.id, choix.action, choix.demande.remplacant_personne_id != null ? { remplacant_personne_id: choix.demande.remplacant_personne_id } : undefined);
      setChoix(null);
      await charger();
      onChange?.();
    } catch (err) {
      setErreur(err instanceof Error ? err.message : "La réponse n'a pas pu être enregistrée. Réessayez.");
    } finally {
      setEnCours(false);
    }
  }

  const seance = (d: Demande) => [formatDateCourt(d.date_atelier), horaire(d)].filter(Boolean).join(' · ');
  const titre = (d: Demande) => (d.atelier_lieu ? `${d.atelier_nom} · ${d.atelier_lieu}` : d.atelier_nom);
  // Le serveur n'offre « se désister » que jusqu'à la veille de la séance (actions_possibles) : l'écran ne refait pas ce calcul.
  const peutSeDesister = (d: Demande) => d.actions_possibles?.includes('refuser_remplacement') === true;

  return (
    <div className="card mb-16" style={{ display: 'grid', gap: 14 }} aria-label="Vos remplacements">
      {demandes.length > 0 && (
        <div className="font-medium text-ink-2" style={{ fontSize: 'var(--t-12, 12px)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Vos remplacements
        </div>
      )}

      {aConfirmer.length > 0 && (
        <div style={{ display: 'grid', gap: 8 }}>
          <div className="font-medium">À confirmer <span className="text-ink-3">{aConfirmer.length}</span></div>
          {aConfirmer.map((d) => {
            const vue = vueDeLaDemande(d, seuils);
            const echeance = ligneEcheance(vue, d.confirmation_remplacant, d.date_atelier);
            const Icone = echeance?.alerte || echeance?.urgent ? AlertTriangle : Hourglass;
            return (
              <div key={d.id} style={{ border: '1px solid var(--accent)', borderLeft: '4px solid var(--accent)', background: 'var(--accent-dim)', borderRadius: 'var(--r1)', padding: '10px 12px', display: 'grid', gap: 6 }}>
                <div className="font-medium">On vous propose de remplacer {d.absent_nom ?? 'un·e collègue'}</div>
                <div className="text-sm text-ink-2">{titre(d)} · {seance(d)}</div>
                {d.designe_par_nom && <div className="text-sm text-ink-2">Désigné·e par {d.designe_par_nom}</div>}
                {echeance && (
                  <div className={`vr-echeance${echeance.urgent ? ' urgent' : ''}`}>
                    <Icone className="ic-16" aria-hidden="true" /><span><TexteSegments segments={echeance.segments} /></span>
                  </div>
                )}
                {etapeEnPhrase(d) && <div className="text-sm text-ink-2">La demande suit déjà son cours : {etapeEnPhrase(d)}.</div>}
                <div className="flex gap-8" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
                  <button type="button" className="btn btn-accent" onClick={() => { setErreur(null); setChoix({ demande: d, action: 'confirmer_remplacement', desistement: false }); }}>Je confirme</button>
                  <button type="button" className="btn" onClick={() => { setErreur(null); setChoix({ demande: d, action: 'refuser_remplacement', desistement: false }); }}>Décliner</button>
                  <button type="button" className="rech-toggle-link" onClick={() => naviguer('demandes', d.id)}>Voir la fiche</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {aVenir.length > 0 && (
        <div style={{ display: 'grid', gap: 8 }}>
          <div className="font-medium">À venir <span className="text-ink-3">{aVenir.length}</span></div>
          {aVenir.map((d) => (
            <div key={d.id} className="flex gap-12" style={{ flexWrap: 'wrap', alignItems: 'baseline', border: '1px solid var(--line)', borderRadius: 'var(--r1)', padding: '8px 12px', fontSize: 'var(--t-13)' }}>
              <span className="font-medium">{titre(d)}</span>
              <span className="text-ink-3">{seance(d)}</span>
              <span>Vous remplacez {d.absent_nom ?? 'un·e collègue'}</span>
              <span className="text-ink-2">{etape(d)}</span>
              <span style={{ marginLeft: 'auto', display: 'flex', gap: 12 }}>
                {peutSeDesister(d) && (
                  <button type="button" className="rech-toggle-link" onClick={() => { setErreur(null); setChoix({ demande: d, action: 'refuser_remplacement', desistement: true }); }}>Me désister</button>
                )}
                <button type="button" className="rech-toggle-link" onClick={() => naviguer('demandes', d.id)}>Voir la fiche</button>
              </span>
            </div>
          ))}
        </div>
      )}

      {choix && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {choix.action === 'confirmer_remplacement' ? 'Confirmer ce remplacement ?' : choix.desistement ? 'Vous désister ?' : 'Décliner ce remplacement ?'}
              </h3>
              <button type="button" className="close-btn" onClick={() => setChoix(null)} aria-label="Fermer" disabled={enCours}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ background: 'var(--bg-inset)', borderRadius: 'var(--r1)', padding: '10px 12px', marginBottom: 12 }}>
                <div className="font-medium">
                  {choix.action === 'confirmer_remplacement'
                    ? `Vous remplacerez ${choix.demande.absent_nom ?? 'un·e collègue'}`
                    : choix.desistement ? `Vous ne remplacerez plus ${choix.demande.absent_nom ?? 'un·e collègue'}` : titre(choix.demande)}
                </div>
                <div className="text-sm text-ink-2">
                  {choix.action === 'confirmer_remplacement' || choix.desistement
                    ? `${titre(choix.demande)} · ${seance(choix.demande)}`
                    : `${seance(choix.demande)} · absence ${dePersonne(choix.demande.absent_nom ?? 'un·e collègue')}`}
                </div>
              </div>
              <p className="text-sm text-ink-2">
                {choix.action === 'confirmer_remplacement'
                  ? (choix.demande.statut === 'atelier_donne' ? 'La passation vous est déjà transmise : vous pourrez la lire dès votre confirmation.'
                    : choix.demande.statut === 'passation' ? 'La passation est déjà ouverte : vous verrez qui débute dès votre confirmation, et vous la lirez quand elle vous sera transmise.'
                    : 'Vous serez prévenu·e dès que tout est validé.')
                  : `${choix.demande.absent_nom ?? 'La personne absente'} sera prévenu·e et la recherche reprendra. Le remplacement disparaîtra de votre liste.${validationsARefaire(choix.demande.statut) ? ` ${validationsARefaire(choix.demande.statut)}` : ''}${choix.desistement ? ' Vous pouvez vous désister jusqu’à la veille de la séance.' : ''}`}
              </p>
              {erreur && <p className="text-sm" style={{ color: 'var(--danger)', marginTop: 12 }}>{erreur}</p>}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn" onClick={() => setChoix(null)} disabled={enCours}>Retour</button>
              <button
                type="button"
                className={`btn ${choix.action === 'confirmer_remplacement' ? 'btn-accent' : 'btn-danger'}`}
                onClick={repondre}
                disabled={enCours}
              >
                {enCours ? <span className="spinner spinner-sm" /> : choix.action === 'confirmer_remplacement' ? 'Oui, je confirme' : choix.desistement ? 'Oui, me désister' : 'Oui, décliner'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
